/**
 * What changed on this chart since this person last looked, as they see it:
 * one set of changes per author, eve's request or a colleague, each step it
 * added, edited or removed and the fields it changed. The chart marks those
 * steps and its bar names the sets.
 *
 * eve's sets are the server's: the room sends her requests on the workflow
 * whole in each `sync` and on every change, each step as it was before the
 * request and as the request left it, so a chart opened mid-request or
 * reloaded marks what one open throughout marks. A colleague's set is folded
 * here from the versions this chart applied that they saved, with the same
 * rule (`stepChangesOf`), so it lasts as long as the tab. The person's own
 * edits, from any of their tabs, an Undo or a Redo included, mark nothing.
 *
 * The person decides how long marks stay. Dismissing eve's request hides it
 * in this browser (`dismissed`, kept by the caller); dismissing a
 * colleague's set forgets it. Undo takes eve's request back, and with it its
 * set. While the room is lost a request still editing reads as unknown after
 * {@link LOST_UNKNOWN_MS}, never as finished, until the room is back.
 *
 * @module components/workflows/workflowChanges
 */
import type { WorkflowAgentRequest, WorkflowNode, WorkflowRequestStep } from "@amoreg/contracts";
import { stepFieldNoun, valueWords } from "@repo/shared/changeWords";
import { foldStepEdits, stepChangesOf, stepFieldChanges, type FieldChange, type StepChange, type StepEdits } from "@repo/shared/workflowDiff";
import { useCallback, useEffect, useMemo, useReducer } from "react";
import * as Schema from "effect/Schema";

import { useLocalStorage } from "../../hooks/useLocalStorage";
import type { AppliedVersion } from "../../state/workflowDoc";
import type { PresenceWorkflowEvent } from "../../state/workflowPresenceChannel";
import type { DocSubscribe } from "./canvas/revisionCues";

export const LOST_UNKNOWN_MS = 90_000;

export type EveState = "editing" | "waiting" | "unknown" | "ended";

export type ChangeAuthor =
  | {
      readonly kind: "eve";
      readonly group: string;
      readonly state: EveState;
      readonly forName: string;
      readonly forColor: string;
      readonly forYou: boolean;
      /** The chat the request came from, when the server names it. */
      readonly threadId: string | null;
    }
  | { readonly kind: "person"; readonly person: string; readonly name: string };

export interface ChangeSet {
  readonly key: string;
  readonly author: ChangeAuthor;
  readonly steps: ReadonlyArray<StepChange<WorkflowNode>>;
}

interface Colleague {
  readonly name: string;
  readonly edits: StepEdits<WorkflowNode>;
}

export interface ChangesState {
  readonly you: string | null;
  readonly requests: ReadonlyArray<WorkflowAgentRequest>;
  readonly colleagues: ReadonlyMap<string, Colleague>;
  readonly lostAt: number | null;
  readonly now: number;
}

export type ChangesEvent =
  | { readonly type: "room"; readonly you: string; readonly requests: ReadonlyArray<WorkflowAgentRequest> }
  | { readonly type: "requests"; readonly requests: ReadonlyArray<WorkflowAgentRequest> }
  | { readonly type: "lost"; readonly at: number }
  | { readonly type: "tick"; readonly at: number }
  | { readonly type: "applied"; readonly versions: ReadonlyArray<Pick<AppliedVersion, "actor" | "reverts" | "before" | "after">> }
  | { readonly type: "forget"; readonly person: string };

export const NO_CHANGES: ChangesState = { you: null, requests: [], colleagues: new Map(), lostAt: null, now: 0 };

export function reduceChanges(state: ChangesState, event: ChangesEvent): ChangesState {
  switch (event.type) {
    case "room":
      return { ...state, you: event.you, requests: event.requests, lostAt: null };
    case "requests":
      return { ...state, requests: event.requests };
    case "lost":
      return state.lostAt === null ? { ...state, lostAt: event.at, now: event.at } : state;
    case "tick":
      return { ...state, now: event.at };
    case "applied": {
      let colleagues = state.colleagues;
      for (const { actor, reverts, before, after } of event.versions) {
        if (actor?.via !== "person" || actor.person === state.you || reverts !== null) continue;
        const known = colleagues.get(actor.person);
        const edits = foldStepEdits(known?.edits ?? new Map(), before, after);
        if (edits === known?.edits) continue;
        colleagues = new Map(colleagues).set(actor.person, { name: actor.name, edits });
      }
      return colleagues === state.colleagues ? state : { ...state, colleagues };
    }
    case "forget": {
      if (!state.colleagues.has(event.person)) return state;
      const colleagues = new Map(state.colleagues);
      colleagues.delete(event.person);
      return { ...state, colleagues };
    }
  }
}

const eveState = (state: ChangesState, request: WorkflowAgentRequest): EveState => {
  if (request.state === "ended") return "ended";
  if (state.lostAt !== null && state.now - state.lostAt >= LOST_UNKNOWN_MS) return "unknown";
  return request.state === "awaiting-approval" ? "waiting" : "editing";
};

/** eve's requests first, as the room lists them, then each colleague with something changed; `dismissed` names eve's requests hidden here. */
export function changeSets(state: ChangesState, dismissed: ReadonlySet<string>): ReadonlyArray<ChangeSet> {
  const eve = state.requests.flatMap((request): ChangeSet[] =>
    dismissed.has(request.group)
      ? []
      : [
          {
            key: `eve:${request.group}`,
            author: {
              kind: "eve",
              group: request.group,
              state: eveState(state, request),
              forName: request.actor.name,
              forColor: request.actor.color,
              forYou: request.actor.person === state.you,
              threadId: threadOf(request),
            },
            steps: request.changes?.map(changeOfStep) ?? request.steps.map(unseenChange),
          },
        ],
  );
  const people = [...state.colleagues].flatMap(([person, { name, edits }]): ChangeSet[] => {
    const steps = stepChangesOf(edits);
    return steps.length === 0 ? [] : [{ key: `person:${person}`, author: { kind: "person", person, name }, steps }];
  });
  return [...eve, ...people];
}

/** The room's requests name no chat yet; a server that adds one is read here. */
const threadOf = (request: WorkflowAgentRequest): string | null =>
  "threadId" in request && typeof request.threadId === "string" && request.threadId.length > 0 ? request.threadId : null;

/** A step a server that sends no contents counted: edited, as far as this chart can tell. */
const unseenChange = (id: string): StepChange<WorkflowNode> => ({ id, kind: "edited", before: null, after: null, fields: [] });

function changeOfStep({ id, before, after }: WorkflowRequestStep): StepChange<WorkflowNode> {
  const kind = before === null ? "added" : after === null ? "removed" : "edited";
  return { id, kind, before, after, fields: before !== null && after !== null ? stepFieldChanges(before, after) : [] };
}

/** How a changed step reads on its card: whether it is new, and how many changes it has. */
export interface StepMark {
  readonly kind: "added" | "edited";
  readonly changes: number;
}

/** Each changed step still on the chart: new if any set added it, with every set's changes to it counted. */
export function stepMarks(sets: ReadonlyArray<ChangeSet>): ReadonlyMap<string, StepMark> {
  const marks = new Map<string, StepMark>();
  for (const { steps } of sets) {
    for (const step of steps) {
      if (step.kind === "removed") continue;
      const known = marks.get(step.id);
      const changes = step.kind === "added" ? 1 : Math.max(1, step.fields.length);
      marks.set(step.id, {
        kind: known?.kind === "added" || step.kind === "added" ? "added" : "edited",
        changes: (known?.changes ?? 0) + changes,
      });
    }
  }
  return marks;
}

export const stepCount = (count: number) => (count === 1 ? "1 step" : `${count} steps`);
export const changeCount = (count: number) => (count === 1 ? "1 change" : `${count} changes`);

/** The bar's words for a set. */
export function setTitle(set: ChangeSet): string {
  const { author, steps } = set;
  const count = steps.length === 0 ? "this workflow" : stepCount(steps.length);
  if (author.kind === "person") return `${author.name} changed ${count}`;
  switch (author.state) {
    case "editing":
      return "eve · editing";
    case "waiting":
      return author.forYou ? "eve · waiting for you" : `eve · waiting for ${author.forName}`;
    case "unknown":
      return "eve · no connection";
    case "ended":
      return `eve changed ${count}`;
  }
}

/** What a set's bar shows: its words, and which of its controls this person gets. */
export interface BarView {
  readonly state: EveState | "person";
  readonly title: string;
  /** The bare count after the title while eve works. */
  readonly count: string | null;
  /** Whether the dot pings. */
  readonly live: boolean;
  /** Whom eve works for, shown by face to everyone else. */
  readonly face: { readonly name: string; readonly color: string } | null;
  readonly details: boolean;
  readonly undo: boolean;
  /** Opens the panel on the waiting card. */
  readonly review: boolean;
  /** The chat to open from the bar's menu. */
  readonly openChat: string | null;
  readonly menu: boolean;
}

export function barView(set: ChangeSet, canUndo: boolean): BarView {
  const { author, steps } = set;
  const state = author.kind === "person" ? "person" : author.state;
  const working = state === "editing" || state === "waiting";
  const eve = author.kind === "eve" ? author : null;
  const ended = state === "ended";
  return {
    state,
    title: setTitle(set),
    count: working && steps.length > 0 ? stepCount(steps.length) : null,
    live: state === "editing",
    face: eve && !eve.forYou ? { name: eve.forName, color: eve.forColor } : null,
    details: !working && steps.length > 0,
    undo: ended && eve !== null && eve.forYou && canUndo,
    review: state === "waiting" && eve !== null && eve.forYou,
    openChat: ended && eve?.forYou ? eve.threadId : null,
    menu: ended || state === "person",
  };
}

/** Whom eve is editing or waiting for on this chart now, once each: her face joins the header's. */
export function eveAtWork(sets: ReadonlyArray<ChangeSet>): ReadonlyArray<string> {
  const names = sets.flatMap(({ author }) => (author.kind === "eve" && (author.state === "editing" || author.state === "waiting") ? [author.forName] : []));
  return [...new Set(names)];
}

/** The longest value shown before and after in the list of changes; a longer one shows only which field changed. */
export const SHORT_VALUE = 48;

export interface FieldLine {
  readonly noun: string;
  /** Both values, when both are short enough to read at a glance. */
  readonly values: { readonly before: string; readonly after: string } | null;
}

const quote = (text: string) => `“${text}”`;

/** One changed field in the list of changes: its name, and its values when they are short. */
export function fieldLine({ field, before, after }: FieldChange): FieldLine {
  const key = field.startsWith("config.") ? field.slice("config.".length) : field;
  const [was, now] = [valueWords(key, before, quote), valueWords(key, after, quote)];
  const short = was !== null && now !== null && !was.includes("…") && !now.includes("…") && was.length <= SHORT_VALUE && now.length <= SHORT_VALUE;
  return { noun: stepFieldNoun(field), values: short ? { before: was, after: now } : null };
}

export function changesOfRoom(event: PresenceWorkflowEvent, at: number): ChangesEvent | null {
  switch (event.type) {
    case "sync":
      return { type: "room", you: event.you, requests: event.requests ?? [] };
    case "requests":
      return { type: "requests", requests: event.requests };
    case "lost":
      return { type: "lost", at };
    case "revision":
      return null;
  }
}

export function nextUnknownAt(state: ChangesState): number | null {
  if (state.lostAt === null || !state.requests.some((request) => request.state !== "ended")) return null;
  const at = state.lostAt + LOST_UNKNOWN_MS;
  return state.now >= at ? null : at;
}

const DISMISSED_KEY = "amoreg:workflow-changes-dismissed";
/** The most dismissed requests this browser remembers; the oldest go first. */
export const DISMISSED_KEPT = 200;
const DismissedRequestList = Schema.Array(Schema.String);
const NONE_DISMISSED: ReadonlyArray<string> = [];

/** eve's requests this person dismissed in this browser, and how to dismiss one. */
export interface DismissedRequests {
  readonly groups: ReadonlySet<string>;
  readonly hide: (group: string) => void;
}

export function useDismissedRequests(): DismissedRequests {
  const [list, setList] = useLocalStorage(DISMISSED_KEY, NONE_DISMISSED, DismissedRequestList);
  const hide = useCallback(
    (group: string) => setList((known) => (known.includes(group) ? known : [...known, group].slice(-DISMISSED_KEPT))),
    [setList],
  );
  const groups = useMemo(() => new Set(list), [list]);
  return useMemo(() => ({ groups, hide }), [groups, hide]);
}

export interface WorkflowChanges {
  readonly sets: ReadonlyArray<ChangeSet>;
  readonly marks: ReadonlyMap<string, StepMark>;
  readonly dismiss: (set: ChangeSet) => void;
}

export function useWorkflowChanges(
  onWorkflowEvent: (listener: (event: PresenceWorkflowEvent) => void) => () => void,
  subscribe: DocSubscribe,
  dismissed: DismissedRequests,
): WorkflowChanges {
  const [state, dispatch] = useReducer(reduceChanges, NO_CHANGES);

  useEffect(
    () =>
      onWorkflowEvent((event) => {
        const next = changesOfRoom(event, Date.now());
        if (next !== null) dispatch(next);
      }),
    [onWorkflowEvent],
  );

  useEffect(
    () =>
      subscribe((before, after) => {
        const versions = after.applied.filter((version) => version.version > before.confirmed.version && !version.mine);
        if (versions.length > 0) dispatch({ type: "applied", versions });
      }),
    [subscribe],
  );

  const due = nextUnknownAt(state);
  useEffect(() => {
    if (due === null) return;
    const timer = window.setTimeout(() => dispatch({ type: "tick", at: Math.max(Date.now(), due) }), Math.max(0, due - Date.now()));
    return () => window.clearTimeout(timer);
  }, [due]);

  const { groups, hide } = dismissed;
  const dismiss = useCallback(
    (set: ChangeSet) => {
      if (set.author.kind === "eve") hide(set.author.group);
      else dispatch({ type: "forget", person: set.author.person });
    },
    [hide],
  );
  const sets = useMemo(() => changeSets(state, groups), [state, groups]);
  const marks = useMemo(() => stepMarks(sets), [sets]);
  return useMemo(() => ({ sets, marks, dismiss }), [sets, marks, dismiss]);
}
